// src/components/ecommerce/ProductManager.jsx - COMPLETE UPDATED VERSION
import React, { useState, useEffect, useMemo } from 'react';
import { 
  Plus,
  Search,
  Filter,
  Grid,
  List,
  Edit,
  Trash2,
  Eye,
  Copy,
  Upload,
  Download,
  Star,
  Package,
  AlertCircle,
  CheckCircle,
  Clock,
  Tag,
  DollarSign,
  Image,
  MoreHorizontal,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  X,
  Save,
  Camera,
  Loader2
} from 'lucide-react';
import { ecommerceService } from '../../services/ecommerceService';
import { ProductForm } from './ProductForm';
import { getStoreTypeConfig } from './utils/storeTypeConfig';
import toast from 'react-hot-toast';

export const ProductManager = ({ storeId, storeData, onUpdate }) => {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [sortBy, setSortBy] = useState('created');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');

  // Get store type configuration
  const storeType = storeData?.storeType || 'general';
  const config = useMemo(() => getStoreTypeConfig(storeType), [storeType]);

  useEffect(() => {
    loadProducts();
    loadCategories();
  }, [storeId]);

  const loadProducts = async () => {
    setLoading(true);
    try {
      const productsData = await ecommerceService.getProducts(storeId);
      setProducts(productsData);
    } catch (error) {
      console.error('Error loading products:', error);
      toast.error(`Failed to load ${config.itemNamePlural.toLowerCase()}`);
    } finally {
      setLoading(false);
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await ecommerceService.getCategories(storeId);
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setShowProductForm(true);
  };

  const handleEditProduct = (product) => {
    setEditingProduct(product);
    setShowProductForm(true);
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm(`Delete this ${config.itemName.toLowerCase()}?`)) return;

    try {
      await ecommerceService.deleteProduct(storeId, productId);
      await loadProducts();
      toast.success(`${config.itemName} deleted successfully`);
      onUpdate?.();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error(`Failed to delete ${config.itemName.toLowerCase()}`);
    }
  };

  const handleDuplicateProduct = async (product) => {
    try {
      const { id, createdAt, updatedAt, ...productData } = product;
      await ecommerceService.addProduct(storeId, {
        ...productData,
        name: `${productData.name} (Copy)`,
        status: 'draft'
      });
      await loadProducts();
      toast.success(`${config.itemName} duplicated successfully`);
      onUpdate?.();
    } catch (error) {
      console.error('Error duplicating product:', error);
      toast.error(`Failed to duplicate ${config.itemName.toLowerCase()}`);
    }
  };

  const handleProductSave = async () => {
    await loadProducts();
    setShowProductForm(false);
    setEditingProduct(null);
    toast.success(`${config.itemName} saved successfully`);
    onUpdate?.();
  };

  // Filter and sort products
  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(product =>
        product.name?.toLowerCase().includes(query) ||
        product.description?.toLowerCase().includes(query) ||
        product.category?.toLowerCase().includes(query)
      );
    }

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(p => p.status === statusFilter);
    }

    // Sort
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'price':
          comparison = (a.price || 0) - (b.price || 0);
          break;
        case 'stock':
          comparison = (a.quantity || 0) - (b.quantity || 0);
          break;
        case 'created':
        default:
          comparison = (a.createdAt?.seconds || 0) - (b.createdAt?.seconds || 0);
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [products, searchQuery, selectedCategory, statusFilter, sortBy, sortOrder]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'inactive': return 'text-gray-600 bg-gray-100';
      case 'draft': return 'text-yellow-600 bg-yellow-100';
      case 'sold_out': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStockStatus = (quantity) => {
    if (quantity === 0) return { color: 'text-red-600', label: 'Out of Stock' };
    if (quantity <= 5) return { color: 'text-orange-600', label: 'Low Stock' };
    return { color: 'text-green-600', label: 'In Stock' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <config.icon className="h-6 w-6 text-blue-600" />
            {config.itemNamePlural}
          </h2>
          <p className="text-gray-600 mt-1">
            {filteredProducts.length} of {products.length} {config.itemNamePlural.toLowerCase()}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => ecommerceService.exportProducts(storeId)}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Download className="h-4 w-4" />
            Export
          </button>
          
          <button
            onClick={handleAddProduct}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            {config.addItemLabel}
          </button>
        </div>
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder={`Search ${config.itemNamePlural.toLowerCase()}...`}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Filters */}
          <div className="flex items-center gap-3">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {categories.map(category => (
                <option key={category.id} value={category.id}>
                  {category.name}
                </option>
              ))}
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="draft">Draft</option>
              <option value="sold_out">Sold Out</option>
            </select>

            <select
              value={`${sortBy}-${sortOrder}`}
              onChange={(e) => {
                const [sort, order] = e.target.value.split('-');
                setSortBy(sort);
                setSortOrder(order);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="created-desc">Newest First</option>
              <option value="created-asc">Oldest First</option>
              <option value="name-asc">Name A-Z</option>
              <option value="name-desc">Name Z-A</option>
              <option value="price-asc">Price Low-High</option>
              <option value="price-desc">Price High-Low</option>
              {config.showInventory && (
                <>
                  <option value="stock-asc">Stock Low-High</option>
                  <option value="stock-desc">Stock High-Low</option>
                </>
              )}
            </select>

            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Products Display */}
      {filteredProducts.length > 0 ? (
        <div className={
          viewMode === 'grid'
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
            : 'space-y-4'
        }>
          {filteredProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              config={config}
              viewMode={viewMode}
              getStatusColor={getStatusColor}
              getStockStatus={getStockStatus}
              onEdit={() => handleEditProduct(product)}
              onDelete={() => handleDeleteProduct(product.id)}
              onDuplicate={() => handleDuplicateProduct(product)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <config.icon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{config.emptyMessage}</h3>
          <p className="text-gray-600 mb-6">
            {searchQuery || selectedCategory !== 'all' || statusFilter !== 'all'
              ? 'Try adjusting your search or filters'
              : config.emptyMessageDetail
            }
          </p>
          <button
            onClick={handleAddProduct}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-4 w-4" />
            {config.addItemLabel}
          </button>
        </div>
      )}

      {/* Product Form Modal */}
      {showProductForm && (
        <ProductForm
          product={editingProduct}
          storeId={storeId}
          storeData={storeData}
          storeTypeConfig={config}
          categories={categories}
          isOpen={showProductForm}
          onClose={() => {
            setShowProductForm(false);
            setEditingProduct(null);
          }}
          onSave={handleProductSave}
        />
      )}
    </div>
  );
};

// Product Card Component
const ProductCard = ({ 
  product, 
  config,
  viewMode, 
  getStatusColor,
  getStockStatus,
  onEdit, 
  onDelete,
  onDuplicate
}) => {
  const Icon = config.icon;
  const stockStatus = config.showInventory ? getStockStatus(product.quantity || 0) : null;

  if (viewMode === 'list') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
        <div className="flex items-center gap-4">
          <div className="w-20 h-20 bg-gray-200 rounded-lg flex-shrink-0 overflow-hidden">
            {product.images?.[0] ? (
              <img
                src={product.images[0]}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Icon className="h-8 w-8 text-gray-400" />
              </div>
            )}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-gray-900 truncate">{product.name}</h3>
            <p className="text-sm text-gray-600 line-clamp-2 mt-1">{product.description}</p>
            <div className="flex items-center gap-4 mt-2">
              {product.price && (
                <span className="text-lg font-bold text-blue-600">
                  ${product.price}
                </span>
              )}
              <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(product.status)}`}>
                {product.status}
              </span>
              {stockStatus && (
                <span className={`text-xs ${stockStatus.color}`}>
                  {stockStatus.label}
                </span>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={onEdit}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
              title="Edit"
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              onClick={onDuplicate}
              className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg"
              title="Duplicate"
            >
              <Copy className="h-4 w-4" />
            </button>
            <button
              onClick={onDelete}
              className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Grid view
  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
      <div className="aspect-square bg-gray-200 relative">
        {product.images?.[0] ? (
          <img
            src={product.images[0]}
            alt={product.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Icon className="h-16 w-16 text-gray-400" />
          </div>
        )}
        {product.featured && (
          <div className="absolute top-2 right-2 bg-yellow-400 text-yellow-900 px-2 py-1 rounded-full text-xs font-semibold flex items-center gap-1">
            <Star className="h-3 w-3" />
            Featured
          </div>
        )}
        <span className={`absolute top-2 left-2 text-xs px-2 py-1 rounded-full ${getStatusColor(product.status)}`}>
          {product.status}
        </span>
      </div>

      <div className="p-4">
        <h3 className="font-semibold text-gray-900 truncate mb-1">{product.name}</h3>
        <p className="text-sm text-gray-600 line-clamp-2 mb-3">{product.description}</p>
        
        <div className="space-y-2 mb-3">
          {product.price && (
            <div className="text-lg font-bold text-blue-600">
              ${product.price}
            </div>
          )}
          {stockStatus && (
            <div className={`text-xs ${stockStatus.color}`}>
              {stockStatus.label} {config.showInventory && `(${product.quantity || 0})`}
            </div>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={onEdit}
            className="flex-1 bg-gray-100 text-gray-700 py-2 px-3 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2 text-sm"
          >
            <Edit className="h-4 w-4" />
            Edit
          </button>
          <button
            onClick={onDuplicate}
            className="bg-green-100 text-green-700 py-2 px-3 rounded-lg hover:bg-green-200"
            title="Duplicate"
          >
            <Copy className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="bg-red-100 text-red-700 py-2 px-3 rounded-lg hover:bg-red-200"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};